import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Skeleton } from "../../Components/Needed";
import { StatusChip } from "../../Components/Kit";
import { downloadText } from "../../Helpers/notificationConsoleApi";
import { duplicateCampaign, exportRun, fetchCampaignReport, fetchRunProgress, getCampaign, retryFailedRun, setCampaignStatus } from "../../Helpers/campaignApi";
import MessagesPanel from "./MessagesPanel";
import { CHANNEL_LABEL, CHANNEL_SHORT, EDITABLE, STATUS_LABEL, STATUS_TONE, pct, reasonText, rupees, scheduleText, whenText } from "./shared";
import "../../Assets/css/notification-centre.css";

// One campaign (Phase 8.9): status and actions, its messages, and the report (Phase 8.7): the funnel per
// channel, opens and clicks, bookings and coupon use in the attribution window, the referral report,
// and each run with "Retry failed now" and a CSV of every person.
const Stat = ({ label, value, sub }) => (
    <div className="border rounded-3 p-3 bg-white h-100">
        <div className="small text-muted">{label}</div>
        <div className="fs-5 fw-semibold">{value}</div>
        {sub ? <div className="small text-muted">{sub}</div> : null}
    </div>
);

const Funnel = ({ funnel }) => {
    const channels = Object.keys(funnel?.byChannel || {});
    if (!channels.length) return <div className="small text-muted">Nothing sent yet.</div>;
    return (
        <div className="table-responsive">
            <table className="table table-sm align-middle">
                <thead><tr><th scope="col">Channel</th><th scope="col">Messages</th><th scope="col">Sent</th><th scope="col">Delivered</th><th scope="col">Read</th><th scope="col">Failed</th><th scope="col">Not sent</th><th scope="col">Waiting</th></tr></thead>
                <tbody>
                    {channels.map((ch) => {
                        const c = funnel.byChannel[ch];
                        const reasons = funnel.reasons?.[ch] || {};
                        return (
                            <tr key={ch}>
                                <th scope="row" className="fw-normal">{CHANNEL_LABEL[ch]}</th>
                                <td>{c.messages}</td>
                                <td>{c.sent} <span className="text-muted small">{pct(c.sent, c.messages)}</span></td>
                                <td>{c.delivered} <span className="text-muted small">{pct(c.delivered, c.messages)}</span></td>
                                <td>{c.read} <span className="text-muted small">{pct(c.read, c.messages)}</span></td>
                                <td title={Object.entries(reasons).map(([r, n]) => `${reasonText(r)}: ${n}`).join("\n")}>{c.failed}</td>
                                <td>{c.skipped}</td>
                                <td>{c.queued}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {channels.some((ch) => Object.keys(funnel.reasons?.[ch] || {}).length) && (
                <div className="small text-muted">
                    {channels.map((ch) => Object.keys(funnel.reasons?.[ch] || {}).length ? <div key={ch}>{CHANNEL_SHORT[ch]}: {Object.entries(funnel.reasons[ch]).map(([r, n]) => `${reasonText(r)} (${n})`).join(", ")}</div> : null)}
                </div>
            )}
        </div>
    );
};

const CampaignDetail = () => {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const [campaign, setCampaign] = useState(null);
    const [report, setReport] = useState(null);
    const [progress, setProgress] = useState({});
    const [busy, setBusy] = useState(false);
    const [tab, setTab] = useState("report");

    const load = useCallback(async () => {
        const [c, r] = await Promise.all([getCampaign(campaignId), fetchCampaignReport(campaignId)]);
        if (!c.ok) { toast.error(c.message); navigate("/campaigns"); return; }
        setCampaign(c.data.campaign);
        setReport(r.ok ? r.data : { error: r.message });
        // Runs still sending: how far they are.
        const running = (r.data?.perRun || []).filter((run) => ["running", "paused", "pending"].includes(run.status));
        const entries = await Promise.all(running.map(async (run) => [run.runId, (await fetchRunProgress(run.runId)).data?.people]));
        setProgress(Object.fromEntries(entries));
    }, [campaignId, navigate]);
    useEffect(() => { load(); }, [load]);
    // While it is sending, refresh every 20 seconds.
    useEffect(() => {
        if (campaign?.status !== "running") return undefined;
        const timer = setInterval(load, 20000);
        return () => clearInterval(timer);
    }, [campaign?.status, load]);

    const move = async (to, confirmText) => {
        if (confirmText && !window.confirm(confirmText)) return;
        setBusy(true);
        const result = await setCampaignStatus(campaignId, to);
        setBusy(false);
        if (!result.ok) { toast.error(result.message); return; }
        (result.data.warnings || []).forEach((w) => toast.info(w.message));
        toast.success("Done");
        load();
    };
    const duplicate = async () => {
        setBusy(true);
        const result = await duplicateCampaign(campaignId);
        setBusy(false);
        if (!result.ok) { toast.error(result.message); return; }
        toast.success("Copied as a new draft");
        navigate(`/campaigns/${result.data.campaign.campaignId}/edit`);
    };
    const retry = async (runId) => {
        const result = await retryFailedRun(runId);
        if (!result.ok) { toast.error(result.message); return; }
        toast.success(result.data.retried ? `${result.data.retried} messages are going again` : "No failed messages to send again");
        load();
    };
    const csv = async (runId) => {
        const result = await exportRun(runId);
        if (!result.ok) { toast.error(result.message); return; }
        downloadText(result.data.filename, result.data.csv);
    };

    if (!campaign) return <Skeleton height={320} />;
    const total = report?.total || {};
    const b = total.bookings || {};
    const ref = total.referrals || {};
    const customer = campaign.audienceType === "customer";
    const actions = [];
    if (EDITABLE.includes(campaign.status)) actions.push(<button key="edit" type="button" className="btn btn-outline-dark btn-sm" onClick={() => navigate(`/campaigns/${campaignId}/edit`)} disabled={busy}>Edit</button>);
    if (campaign.status === "draft") actions.push(<button key="start" type="button" className="btn btn-primary btn-sm" onClick={() => navigate(`/campaigns/${campaignId}/edit`)} disabled={busy}>Review and start</button>);
    if (["scheduled", "running"].includes(campaign.status)) actions.push(<button key="pause" type="button" className="btn btn-outline-warning btn-sm" onClick={() => move("paused")} disabled={busy}>Pause</button>);
    if (campaign.status === "paused") actions.push(<button key="resume" type="button" className="btn btn-primary btn-sm" onClick={() => move("running")} disabled={busy}>Resume</button>);
    if (campaign.status === "scheduled") actions.push(<button key="draft" type="button" className="btn btn-outline-secondary btn-sm" onClick={() => move("draft")} disabled={busy}>Back to draft</button>);
    actions.push(<button key="dup" type="button" className="btn btn-outline-secondary btn-sm" onClick={duplicate} disabled={busy}>Duplicate</button>);
    if (!["completed", "cancelled"].includes(campaign.status)) actions.push(<button key="cancel" type="button" className="btn btn-outline-danger btn-sm" onClick={() => move("cancelled", "Cancel this campaign? Messages not sent yet are dropped. This can't be undone.")} disabled={busy}>Cancel campaign</button>);

    return (
        <div className="settings-page">
            <button type="button" className="btn btn-link btn-sm px-0" onClick={() => navigate("/campaigns")}>&larr; All campaigns</button>
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                <div>
                    <h4 className="fw-semibold mb-1">{campaign.name} <StatusChip tone={STATUS_TONE[campaign.status] || "neutral"}>{STATUS_LABEL[campaign.status]}</StatusChip></h4>
                    <div className="small text-muted">
                        {customer ? "Customers" : "Staff"} · {campaign.purpose === "service" ? "Service" : "Promotional"} · {(campaign.channels || []).map((c) => CHANNEL_SHORT[c]).join(", ")} · {scheduleText(campaign.schedule)}
                        {campaign.nextRunAt ? ` · next ${whenText(campaign.nextRunAt)}` : ""}{campaign.coupon?.couponCode ? ` · coupon ${campaign.coupon.couponCode}` : ""}{campaign.shadow ? " · shadow" : ""}
                    </div>
                </div>
                <div className="d-flex flex-wrap gap-2">{actions}</div>
            </div>

            <div className="d-flex gap-2 mb-3" role="tablist">
                {[["report", "Report"], ["messages", "Messages"]].map(([key, label]) => (
                    <button key={key} type="button" role="tab" aria-selected={tab === key} className={`nc-pill ${tab === key ? "is-active" : ""}`} onClick={() => setTab(key)}>{label}</button>
                ))}
            </div>

            {tab === "messages" && <div className="bg-white border rounded-3 p-3"><MessagesPanel campaignId={campaignId} /></div>}

            {tab === "report" && (!report ? <Skeleton height={240} /> : report.error ? <div className="text-danger">{report.error}</div> : (
                <>
                    <div className="row g-3 mb-3">
                        <div className="col-6 col-lg-3"><Stat label="Reached" value={(total.eligible || 0).toLocaleString("en-IN")} sub={`of ${(total.audience || 0).toLocaleString("en-IN")} in the audience`} /></div>
                        <div className="col-6 col-lg-3"><Stat label="Opened (push)" value={total.opened || 0} sub={pct(total.opened, total.eligible)} /></div>
                        <div className="col-6 col-lg-3"><Stat label="Clicked the link" value={total.clicked?.people || 0} sub={`${total.clicked?.clicks || 0} clicks`} /></div>
                        {customer && <div className="col-6 col-lg-3"><Stat label="Bookings confirmed" value={b.confirmed || 0} sub={`${rupees(b.revenue)} · ${b.made || 0} made`} /></div>}
                    </div>

                    <div className="bg-white border rounded-3 p-3 mb-3">
                        <div className="fw-semibold mb-2">Funnel per channel</div>
                        <Funnel funnel={total.funnel} />
                        {Object.keys(total.excluded || {}).length > 0 && <div className="small text-muted">Left out: {Object.entries(total.excluded).map(([r, n]) => `${reasonText(r)} (${n})`).join(", ")}</div>}
                    </div>

                    {customer && (
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <div className="bg-white border rounded-3 p-3 h-100">
                                    <div className="fw-semibold mb-2">Bookings in the {report.campaign.attributionDays}-day window</div>
                                    <div className="small">{b.made || 0} made, {b.confirmed || 0} confirmed by {b.people || 0} people, {rupees(b.revenue)} revenue.</div>
                                    {report.campaign.coupon ? <div className="small mt-1">Coupon {report.campaign.coupon}: used {b.couponRedemptions || 0} times, {rupees(b.couponDiscount)} discount.</div> : null}
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="bg-white border rounded-3 p-3 h-100">
                                    <div className="fw-semibold mb-2">Referrals in the window</div>
                                    <div className="small">{ref.signups || 0} people signed up with a recipient's referral code ({ref.referrers || 0} recipients referred someone).</div>
                                    <div className="small">{ref.firstBookings || 0} of them booked ({rupees(ref.revenue)}). {ref.paidPayouts || 0} referral payouts paid to recipients ({ref.coinsToRecipients || 0} coins).</div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="bg-white border rounded-3 p-3">
                        <div className="fw-semibold mb-2">Runs</div>
                        {!(report.perRun || []).length ? <div className="small text-muted">No runs yet.</div> : (
                            <div className="table-responsive">
                                <table className="table table-sm align-middle mb-0">
                                    <thead><tr><th scope="col">Started</th><th scope="col">Status</th><th scope="col">People</th><th scope="col">Sent</th><th scope="col">Clicked</th>{customer && <th scope="col">Bookings</th>}<th scope="col"><span className="visually-hidden">Actions</span></th></tr></thead>
                                    <tbody>
                                        {report.perRun.map((run) => {
                                            const sent = Object.values(run.funnel?.byChannel || {}).reduce((sum, c) => sum + c.sent, 0);
                                            const failed = Object.values(run.funnel?.byChannel || {}).reduce((sum, c) => sum + c.failed, 0);
                                            const p = progress[run.runId];
                                            return (
                                                <tr key={run.runId}>
                                                    <td className="small">{whenText(run.startedAt)}{run.shadow ? <span className="nc-sub"> · shadow</span> : null}</td>
                                                    <td className="small">{run.status}{p ? ` (${p.done}/${p.total})` : ""}</td>
                                                    <td className="small">{run.eligible} <span className="text-muted">of {run.audience}</span></td>
                                                    <td className="small">{sent}{failed ? <span className="text-danger"> · {failed} failed</span> : null}</td>
                                                    <td className="small">{run.clicked?.people || 0}</td>
                                                    {customer && <td className="small">{run.bookings ? `${run.bookings.confirmed} (${rupees(run.bookings.revenue)})` : "-"}</td>}
                                                    <td className="text-end text-nowrap">
                                                        {failed > 0 && !run.shadow && <button type="button" className="btn btn-outline-primary btn-sm me-1" onClick={() => retry(run.runId)}>Retry failed now</button>}
                                                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => csv(run.runId)}>CSV</button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </>
            ))}
        </div>
    );
};

export default CampaignDetail;
